import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, Download, ExternalLink, Mail, Search, X as XIcon, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import TarifGridDisplay from './TarifGridDisplay';

const API = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${API}/api/docs/grille-tarifaire`;

// Grille officielle SESAM figée — tarifs de référence 2026 (identiques au PDF officiel)
const OFFICIAL_GRID = {
  nom: 'Grille tarifaire officielle SESAM 2026',
  sections: [
    {
      type_panneau: 'Panneau déroulant',
      items: [
        { label: '1 affiche', prix_jour: 3000, prix_mois: 90000, prix_annee: 1080000 },
        { label: '1 panneau (10 affiches)', prix_jour: 30000, prix_mois: 900000, prix_annee: 2700000 },
        { label: '10 emplacements (10 panneaux)', prix_jour: 300000, prix_mois: 9000000, prix_annee: 108000000 },
      ],
    },
    {
      type_panneau: 'Panneau LED',
      items: [
        { label: '1 affiche', prix_jour: 2000, prix_mois: 60000, prix_annee: 720000 },
        { label: '1 panneau (10 affiches)', prix_jour: 20000, prix_mois: 600000, prix_annee: 1800000 },
        { label: '3 emplacements (3 panneaux)', prix_jour: 60000, prix_mois: 1800000, prix_annee: 21600000 },
      ],
    },
    {
      type_panneau: 'Grand format',
      items: [
        { label: '1 panneau', prix_jour: 10000, prix_mois: 300000, prix_annee: 3600000 },
        { label: '1 emplacement total', prix_jour: 300000, prix_mois: 900000, prix_annee: 3600000 },
      ],
    },
  ],
};

export default function GrilleTarifaire() {
  const [showSendModal, setShowSendModal] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // prospect sélectionné
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!showSendModal) return;
    (async () => {
      try {
        // On charge Prospects + Clients (les deux sont des destinataires possibles)
        const [pRes, cRes] = await Promise.all([
          axios.get(`${API}/api/prospects`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/clients`).catch(() => ({ data: [] })),
        ]);
        const merged = [
          ...(pRes.data || []).map((p) => ({ ...p, _type: 'Prospect' })),
          ...(cRes.data || []).map((c) => ({ ...c, _type: 'Client' })),
        ];
        setProspects(merged);
      } catch (e) {
        toast.error('Erreur de chargement des prospects');
      }
    })();
  }, [showSendModal]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prospects;
    return prospects.filter((p) => {
      const hay = [p.entreprise, p.contact, p.email, p.telephone, p._type].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [prospects, search]);

  const download = () => {
    const link = document.createElement('a');
    link.href = PDF_URL;
    link.download = 'Grille_tarifaire_SESAM.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeModal = () => {
    setShowSendModal(false);
    setSelected(null);
    setSearch('');
    setMessage('');
  };

  const sendViaResend = async () => {
    if (!selected?.email) {
      toast.error("Ce contact n'a pas d'adresse email");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/docs/grille-tarifaire/send-email`, {
        recipient_email: selected.email,
        recipient_name: selected.contact || selected.entreprise || '',
        message,
      });
      if (r.data?.success) {
        toast.success(r.data.message || 'Grille tarifaire envoyée par email');
        closeModal();
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const sendViaGmail = async () => {
    if (!selected?.email) {
      toast.error("Ce contact n'a pas d'adresse email");
      return;
    }
    // Télécharger le PDF pour l'utilisateur (à joindre manuellement)
    try {
      const blob = await axios.get(PDF_URL, { responseType: 'blob' }).then((r) => r.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Grille_tarifaire_SESAM.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { /* silencieux */ }

    // Ouvrir Gmail (nouvelle fenêtre) avec expéditeur = sesambangui@gmail.com, destinataire, sujet, corps pré-remplis
    const subject = encodeURIComponent('Grille tarifaire officielle — SESAM RCA');
    const body = encodeURIComponent(
      `Bonjour ${selected.contact || selected.entreprise || ''},\n\n` +
      `Veuillez trouver ci-joint notre grille tarifaire officielle SESAM pour 2026.\n\n` +
      (message ? message + '\n\n' : '') +
      `Cordialement,\nL'équipe SESAM RCA\nsesambangui@gmail.com · 75 50 37 02 / 74 36 11 36`
    );
    const to = encodeURIComponent(selected.email);
    // Gmail compose : authuser=sesambangui@gmail.com pour forcer le bon compte
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&authuser=sesambangui@gmail.com&to=${to}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    toast.info('Gmail ouvert dans un nouvel onglet. Le PDF a été téléchargé — glissez-le dans le mail avant d\'envoyer.');
  };

  return (
    <>
      <Card className="border-2 border-[#0B1E42] shadow-lg" data-testid="grille-tarifaire-card">
        <CardHeader className="bg-gradient-to-r from-[#0B1E42] to-[#1e3a8a] text-white">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-3">
              <FileText className="h-6 w-6" />
              <div>
                <div className="text-xl font-bold">Grille tarifaire officielle SESAM</div>
                <p className="text-sm text-blue-100 font-normal">Tarifs 2026 · Panneaux déroulants, LED intérieur, grand format</p>
              </div>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-white text-[#0B1E42] font-bold">Officiel</Badge>
              <Button
                onClick={() => setShowSendModal(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                data-testid="btn-send-grille"
              >
                <Mail className="h-4 w-4" /> Envoyer par mail
              </Button>
              <Button onClick={download} className="bg-white text-[#0B1E42] hover:bg-blue-50 gap-2" data-testid="btn-download-grille">
                <Download className="h-4 w-4" /> Télécharger PDF
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#0B1E42] gap-2">
                <a href={PDF_URL} target="_blank" rel="noreferrer" data-testid="btn-open-grille">
                  <ExternalLink className="h-4 w-4" /> Ouvrir
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 bg-slate-50">
          <TarifGridDisplay
            nom={OFFICIAL_GRID.nom}
            sections={OFFICIAL_GRID.sections}
            showTitle={true}
            className="shadow-sm"
          />
          <div className="mt-3 text-center">
            <a href={PDF_URL} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline" data-testid="btn-open-official-pdf">
              Ouvrir le PDF officiel dans un nouvel onglet
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Modal — sélection prospect/client puis choix du mode d'envoi */}
      <Dialog open={showSendModal} onOpenChange={(v) => { if (!v) closeModal(); }}>
        <DialogContent className="max-w-2xl" data-testid="send-grille-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0B1E42]">
              <Mail className="h-5 w-5" /> Envoyer la grille tarifaire
            </DialogTitle>
            <DialogDescription>
              {selected
                ? `Destinataire : ${selected.entreprise || selected.contact} · ${selected.email || 'sans email'}`
                : 'Sélectionnez un prospect ou un client dans la liste ci-dessous.'}
            </DialogDescription>
          </DialogHeader>

          {!selected && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par entreprise, contact ou email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="grille-search"
                />
              </div>
              <div className="border rounded-lg divide-y max-h-80 overflow-y-auto" data-testid="grille-prospects-list">
                {filtered.length === 0 && (
                  <div className="text-center text-sm text-slate-500 py-8">Aucun prospect / client trouvé.</div>
                )}
                {filtered.map((p) => (
                  <button
                    key={`${p._type}-${p.id}`}
                    onClick={() => setSelected(p)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 focus:outline-none focus:bg-blue-50"
                    data-testid={`grille-select-${p.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0">
                      {(p.entreprise || p.contact || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-[#0B1E42] truncate">{p.entreprise || p.contact || '—'}</div>
                      <div className="text-xs text-slate-500 truncate">{p.contact ? p.contact + ' · ' : ''}{p.email || 'pas d\'email'}</div>
                    </div>
                    <Badge className={p._type === 'Client' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {p._type}
                    </Badge>
                  </button>
                ))}
              </div>
            </>
          )}

          {selected && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-[#0B1E42]">{selected.entreprise || selected.contact}</div>
                  <div className="text-sm text-slate-600">{selected.email || <span className="text-rose-600">Aucun email renseigné !</span>}</div>
                  <div className="text-xs text-slate-500">{selected.telephone || ''}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="grille-change-recipient">
                  <XIcon className="h-4 w-4 mr-1" /> Changer
                </Button>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Message personnalisé (optionnel)</label>
                <Textarea
                  placeholder="Ex : Suite à notre échange, veuillez trouver notre grille tarifaire…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  data-testid="grille-message"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={sendViaResend}
                  disabled={!selected.email || sending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-11"
                  data-testid="btn-send-resend"
                >
                  <Send className="h-4 w-4" />
                  {sending ? 'Envoi en cours…' : 'Envoyer automatiquement'}
                </Button>
                <Button
                  onClick={sendViaGmail}
                  disabled={!selected.email}
                  variant="outline"
                  className="border-blue-500 text-blue-700 hover:bg-blue-50 gap-2 h-11"
                  data-testid="btn-send-gmail"
                >
                  <Mail className="h-4 w-4" />
                  Ouvrir Gmail + télécharger PDF
                </Button>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                <strong>Envoi automatique</strong> : le PDF part immédiatement depuis <code>sesambangui@gmail.com</code> avec la grille en pièce jointe.
                <br/>
                <strong>Gmail + PDF</strong> : la grille se télécharge automatiquement, puis Gmail s&apos;ouvre pré-rempli — il ne vous reste qu&apos;à glisser le PDF avant d&apos;envoyer.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeModal}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
